import { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { getAccount, getPremium, type AccountInfo, type PremiumInfo } from '../services/accountService';
import { getCash, type CashAccount } from '../services/cashService';
import { getGmInfo, type GmInfo } from '../services/authService';
import { getRmtMe } from '../services/rmtService';
import { getStreamerMe } from '../services/streamerService';

export interface AuthUser {
  username: string;
  token: string;
  expiration?: string;
}

interface AuthContextProps {
  user: AuthUser | null;
  account: AccountInfo | null;
  cash: CashAccount | null;
  premium: PremiumInfo | null;
  gmInfo: GmInfo | null;
  isLoggedIn: boolean;
  isGm: boolean;
  isSuper: boolean;
  isSeller: boolean;
  isStreamer: boolean;
  hasFeature: (feature: string) => boolean;
  login: (user: AuthUser) => void;
  logout: () => void;
  refreshCash: () => Promise<void>;
  refreshPremium: () => Promise<void>;
  refreshGmInfo: () => Promise<void>;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

const STORAGE_KEY = 'liberty_auth';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return null;
      const parsed = JSON.parse(stored) as AuthUser;
      if (parsed.expiration && new Date(parsed.expiration) < new Date()) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  });

  const [account, setAccount] = useState<AccountInfo | null>(null);
  const [cash, setCash] = useState<CashAccount | null>(null);
  const [premium, setPremium] = useState<PremiumInfo | null>(null);
  const [gmInfo, setGmInfo] = useState<GmInfo | null>(null);
  const [isSeller, setIsSeller] = useState(false);
  const [isStreamer, setIsStreamer] = useState(false);

  const login = useCallback((u: AuthUser) => {
    setUser(u);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(u));
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setAccount(null);
    setCash(null);
    setPremium(null);
    setGmInfo(null);
    setIsSeller(false);
    setIsStreamer(false);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const refreshCash = useCallback(async () => {
    try {
      const c = await getCash();
      setCash(c);
    } catch { /* ignore */ }
  }, []);

  const refreshPremium = useCallback(async () => {
    try {
      const p = await getPremium();
      setPremium(p);
    } catch { /* ignore */ }
  }, []);

  const refreshGmInfo = useCallback(async () => {
    try {
      const info = await getGmInfo();
      setGmInfo(info);
    } catch {
      setGmInfo(null);
    }
  }, []);

  // Fetch account data when user is set (login or page load)
  useEffect(() => {
    if (!user?.token) {
      setAccount(null);
      setCash(null);
      setPremium(null);
      setGmInfo(null);
      setIsSeller(false);
      setIsStreamer(false);
      return;
    }
    getAccount().then(setAccount).catch(() => setAccount(null));
    getCash().then(setCash).catch(() => setCash(null));
    getPremium().then(setPremium).catch(() => setPremium(null));
    getGmInfo().then(setGmInfo).catch(() => setGmInfo(null));
    getRmtMe().then(m => setIsSeller(m.isSeller)).catch(() => setIsSeller(false));
    // Probe streamer: 200 → é streamer; 404/erro → não é. Nunca bloqueia o login.
    getStreamerMe().then(() => setIsStreamer(true)).catch(() => setIsStreamer(false));
  }, [user]);

  // Auto-logout on token expiration
  useEffect(() => {
    if (!user?.expiration) return;
    const ms = new Date(user.expiration).getTime() - Date.now();
    if (ms <= 0) { logout(); return; }
    const t = setTimeout(logout, ms);
    return () => clearTimeout(t);
  }, [user, logout]);

  const isGm = !!gmInfo && gmInfo.gmLevel >= 1;
  const isSuper = !!gmInfo && gmInfo.gmLevel >= 99;
  const hasFeature = useCallback(
    (f: string) => !!gmInfo && gmInfo.features.includes(f),
    [gmInfo],
  );

  return (
    <AuthContext.Provider value={{
      user, account, cash, premium, gmInfo,
      isLoggedIn: !!user, isGm, isSuper, isSeller, isStreamer, hasFeature,
      login, logout, refreshCash, refreshPremium, refreshGmInfo,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
